import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { GripVertical, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import FactDialog from "@/components/hfacs/FactDialog";
import FactStatusBadge from "@/components/hfacs/FactStatusBadge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ChronologyTab({ analysisId }) {
  const [facts, setFacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [dragId, setDragId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/hfacs/${analysisId}/facts`);
      setFacts(res.data);
    } catch (e) {
      toast.error("Impossible de charger la chronologie.");
    } finally {
      setLoading(false);
    }
  }, [analysisId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (form) => {
    setSubmitting(true);
    try {
      if (editing) {
        await axios.put(`${API}/hfacs/${analysisId}/facts/${editing.ID_FAIT}`, form);
      } else {
        await axios.post(`${API}/hfacs/${analysisId}/facts`, form);
      }
      toast.success("Fait enregistré.");
      setDialogOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement.");
    } finally {
      setSubmitting(false);
    }
  };

  const onDrop = async (targetId) => {
    if (!dragId || dragId === targetId) return;
    const ids = facts.map((f) => f.ID_FAIT);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    const reordered = ids.map((id) => facts.find((f) => f.ID_FAIT === id));
    setFacts(reordered);
    setDragId(null);
    try {
      await axios.put(`${API}/hfacs/${analysisId}/facts/reorder`, { ordered_fact_ids: ids });
    } catch (e) {
      toast.error("Erreur lors de la réorganisation.");
      load();
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button
          data-testid="timeline-add-fact-button"
          onClick={() => { setEditing(null); setDialogOpen(true); }}
          className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2"
        >
          <Plus className="h-4 w-4" />
          Ajouter un fait
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Chargement…</p>
      ) : (
        <div className="space-y-2" data-testid="timeline-list">
          {facts.map((f) => (
            <div
              key={f.ID_FAIT}
              draggable
              onDragStart={() => setDragId(f.ID_FAIT)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(f.ID_FAIT)}
              data-testid={`fact-row-${f.ID_FAIT}`}
              className="flex items-start gap-3 bg-card border border-border rounded-md p-4 cursor-pointer hover:border-border-highlight transition-colors"
              onClick={() => {
                setEditing({
                  ID_FAIT: f.ID_FAIT,
                  date: f.DATE, heure: f.HEURE, description_factuelle: f.DESCRIPTION_FACTUELLE,
                  source: f.SOURCE, fonction_source: f.FONCTION_SOURCE, type_source: f.TYPE_SOURCE,
                  statut_verification: f.STATUT_VERIFICATION, commentaire: f.COMMENTAIRE, url_preuve: f.URL_PREUVE,
                });
                setDialogOpen(true);
              }}
            >
              <GripVertical className="h-4 w-4 text-muted-foreground mt-1 shrink-0 cursor-grab" />
              <div className="flex-1">
                <div className="flex items-center justify-between gap-3 mb-1">
                  <span className="font-mono text-xs text-muted-foreground">{f.DATE} {f.HEURE}</span>
                  <FactStatusBadge status={f.STATUT_VERIFICATION} />
                </div>
                <p className="text-sm">{f.DESCRIPTION_FACTUELLE}</p>
                {f.SOURCE && <p className="text-xs text-muted-foreground mt-1">Source : {f.SOURCE} {f.FONCTION_SOURCE && `(${f.FONCTION_SOURCE})`}</p>}
              </div>
            </div>
          ))}
          {facts.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">Aucun fait relevé pour l'instant.</p>
          )}
        </div>
      )}

      <FactDialog open={dialogOpen} onOpenChange={setDialogOpen} analysisId={analysisId} initial={editing} onSubmit={submit} submitting={submitting} />
    </div>
  );
}
